import React, { useState, useEffect, useRef, useMemo,useCallback } from 'react';
import {
    TextField,
    Autocomplete,
    Popper,
    Button,
    Tooltip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { styled } from '@mui/system';
import CustomDatePicker from '../CustomDates/CustomDatePicker/CustomDatePicker';
import CustomMonthDatePicker from '../CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import DocumentService from '../../../Services/documents.service';
import { toast } from 'react-toastify';
import AddAccountCardModal from '../AddAccountCardModal/AddAccountCardModal';
import CreateDepreciationSortCodeDialog from './CreateDepreciationSortCodeDialog';
import EnumsService from '../../../Services/enums.service';
import Constants from '../../../assests/Constants/constants';
import { useHamburgerMenu } from '../../../Contexts/HamburgerMenuContext';
import AssetModalShell from '../../Assets/SubComponents/AssetModalShell/AssetModalShell';
import shell from '../../Assets/SubComponents/AssetModalShell/AssetModalShell.module.scss';
import { xtramInputProps, xtramTextAlign, xtramDateInputSx, xtramPopperSx } from '../../Assets/SubComponents/AssetModalShell/assetModalInputs';
import { beautifyNumber } from '../../../Utils/FormatNumber';

const StyledPopper = styled(Popper)(xtramPopperSx);

const StyledListItem = styled('li')({
    cursor: 'pointer',
});

const CreateDepreciationJournalEntryDialog = ({
    open,
    onClose,
    client,
    token,
    direction = 'rtl',
    sortCodeIdOptions = [],
    debitAccountOptions = [],
    reportDate,
    depreciationDifference = 0,
    loading = false,
    setLoading,
    onSaveSuccess,
    assetGroups = [],
    initialSortCodeId = null, // Optional: pre-select a sort code when dialog opens
    initialAssetGroup = null, // Optional: asset group to pre-fill debit/credit accounts
    journalEntries = [], // Journal entries to calculate depreciation difference
    sortCodeIdOptions: parentSortCodeIdOptions = [], // Sort codes from parent to calculate depreciation difference
    selectedAssetGroups = [], // Optional: selected asset groups from multi-select
    getAssetGroupDepreciationDifference = null // Optional: helper function to calculate depreciation difference for a group
}) => {
    const { t } = useTranslation();
    const { refreshEnumsCallback } = useHamburgerMenu();
    const [createFormData, setCreateFormData] = useState({
        sortCodeId: '',
        debitAccount: '',
        creditAccount: '',
        reportingMonthDate: null,
        registrationMonthDate: null,
        documentDate: null,
        uploadDate: null,
        amount: '',
        vat: 0, // Default VAT to 0 for depreciation journal entries
        reference: '',
        reference2: '',
        description: '',
        accountCaseId: '',
        confirmationNumber: '',
        journalEntryType: 1 // Default to IsDepreciationJournal
    });

    // For controlling AddAccountCard logic
    const [searchTermCredit, setSearchTermCredit] = useState('');
    const [searchTermDebit, setSearchTermDebit] = useState('');
    const [editedAddAccountCard, setEditedAddAccountCard] = useState(false);
    const [isEditAccountCard, setIsEditAccountCard] = useState(false);
    const [addAccountCardType, setAddAccountCardType] = useState('creditAccount');
    const [cardIdOption, setCardIdOption] = useState(0);

    // Local state for account options (can be updated when new account is added)
    const [localDebitAccountOptions, setLocalDebitAccountOptions] = useState(debitAccountOptions);
    const [localSortCodeIdOptions, setLocalSortCodeIdOptions] = useState(sortCodeIdOptions);

    // State for CreateDepreciationSortCodeDialog
    const [isCreateSortCodeDialogOpen, setIsCreateSortCodeDialogOpen] = useState(false);
    
    // State to track if we're auto-setting up accounts from initialAssetGroup
    const [isSettingUpAccounts, setIsSettingUpAccounts] = useState(false);
    const [pendingAccountSetup, setPendingAccountSetup] = useState(null); // 'debit' | 'credit' | null
    const hasProcessedInitialAssetGroupRef = useRef(false); // Track if we've already processed initialAssetGroup
    
    // State for amount validation
    const [amountError, setAmountError] = useState(false);
    const [maxAllowedAmount, setMaxAllowedAmount] = useState(0);

    // Sync local options with props when they change
    useEffect(() => {
        setLocalDebitAccountOptions(debitAccountOptions);
    }, [debitAccountOptions]);

    useEffect(() => {
        setLocalSortCodeIdOptions(sortCodeIdOptions);
    }, [sortCodeIdOptions]);

    // The new account card data that will be passed to AddAccountCardModal
    const [newAccountCard, setNewAccountCard] = useState({
        accountCodeName: '',
        accountCodeId: '',
        accountCaseId: '',
    });

    // Helper function to calculate depreciation difference for an asset group
    const calculateAssetGroupDepreciationDifference = (group) => {
        if (!group) return 0;
        
        // Find the sort code for this asset group
        const expectedSortCodeName = `${Constants.DepreciationPrefixes.SortCode}${group.assetGroupName}`;
        const sortCode = parentSortCodeIdOptions.find(
            sc => sc.sortCodeName === expectedSortCodeName
        );
        
        if (!sortCode) {
            // No sort code exists for this group yet, so no journal entries
            return group.sumDepreciationThisYear || 0;
        }
        
        // Find all journal entries with this sortCodeId
        const groupJournalEntries = journalEntries.filter(
            entry => String(entry.sortCodeId) === String(sortCode.sortCodeId)
        );
        
        // Sum the amounts of journal entries for this group
        const sumJournalEntryAmounts = groupJournalEntries.reduce(
            (sum, entry) => sum + (entry.amount || 0),
            0
        );
        
        // Calculate difference: total depreciation - sum of journal entry amounts
        return (group.sumDepreciationThisYear || 0) - sumJournalEntryAmounts;
    };

    // Helper function to get the next unique cardId dynamically
    // This ensures uniqueness even if accounts are created in sequence
    const getNextUniqueCardId = useCallback(() => {
        if (localDebitAccountOptions.length === 0) {
            return 1;
        }
        
        // Get all existing cardIds as numbers
        const existingCardIds = localDebitAccountOptions
            .map((opt) => parseInt(opt.cardId, 10))
            .filter((id) => !isNaN(id) && id > 0)
            .sort((a, b) => b - a); // Sort descending
        
        if (existingCardIds.length === 0) {
            return 1;
        }
        
        // Find the next available ID (max + 1)
        const maxCardId = existingCardIds[0];
        let nextId = maxCardId + 1;
        
        // Double-check for uniqueness (in case of gaps in the sequence)
        // This is a safety check - AddAccountCardModal will also validate
        while (existingCardIds.includes(nextId)) {
            nextId++;
        }
        
        return nextId;
    }, [localDebitAccountOptions]);

    // Initialize form data when dialog opens
    useEffect(() => {
        if (open) {
            const currentDate = new Date();
            const reportDateObj = reportDate ? dayjs.utc(reportDate).toDate() : currentDate;
            const selectedYear = reportDate ? dayjs.utc(reportDate).year() : currentDate.getFullYear();
            // Noon keeps the date on Dec 31 after the UTC conversion in toISOString.
            const endOfSelectedYear = new Date(selectedYear, 11, 31, 12, 0, 0);
            
            // Calculate amount based on priority:
            // 1. If selectedAssetGroups are provided, sum their depreciation differences
            // 2. If initialAssetGroup is provided, use its depreciation difference
            // 3. Otherwise use the depreciationDifference prop
            let calculatedAmount = depreciationDifference;
            

            if (selectedAssetGroups.length > 0 && getAssetGroupDepreciationDifference) {
                // Sum depreciation differences for all selected asset groups
                // Round each group's depreciation difference to nearest integer before summing
                calculatedAmount = selectedAssetGroups.reduce((sum, group, index) => {
                    // First, round the sumDepreciationThisYear to nearest integer
                    const roundedSumDepreciation = Math.round(group.sumDepreciationThisYear || 0);
                    
                    // Get the depreciation difference (which already accounts for journal entries)
                    const groupDepreciationDiff = getAssetGroupDepreciationDifference(group);
                    
                    // Round the depreciation difference to nearest integer
                    const roundedDiff = Math.round(groupDepreciationDiff || 0);

                    
                    return sum + roundedDiff;
                }, 0);
            } else if (initialAssetGroup) {
                const groupDifference = calculateAssetGroupDepreciationDifference(initialAssetGroup);
                calculatedAmount = Math.round(groupDifference);
            } else {
                console.log('--- Using depreciationDifference prop ---');
                console.log('Using default depreciationDifference:', calculatedAmount);
            }
            
            // Set amount value - round to nearest integer (no decimal digits)
            const formattedAmount = calculatedAmount ? Math.round(calculatedAmount) : '';

            
            // Set the maximum allowed amount (depreciationDifference) for validation
            const maxAmount = Math.round(calculatedAmount || 0);
            setMaxAllowedAmount(maxAmount);
            setAmountError(false); // Reset error state when dialog opens
            
            // Determine sortCodeId: use initialSortCodeId if provided, otherwise default to '905'
            const sortCodeIdValue = initialSortCodeId || '905';
            
            // Determine debitAccount and creditAccount:
            // - If multiple asset groups are selected, don't pre-fill accounts (leave empty)
            // - Otherwise, use initialDebitAccount/initialCreditAccount if provided
            const isMultipleGroups = selectedAssetGroups.length > 1;
            const debitAccountValue = isMultipleGroups ? '' : '';
            const creditAccountValue = isMultipleGroups ? '' : '';
            
            setCreateFormData({
                sortCodeId: sortCodeIdValue,
                debitAccount: debitAccountValue,
                creditAccount: creditAccountValue,
                reportingMonthDate: reportDateObj,
                registrationMonthDate: reportDateObj,
                documentDate: endOfSelectedYear,
                uploadDate: currentDate,
                amount: formattedAmount,
                vat: 0, // Default VAT to 0 for depreciation journal entries
                reference: '',
                reference2: '',
                description: '',
                accountCaseId: '',
                confirmationNumber: '',
                journalEntryType: 1
            });
            setIsSettingUpAccounts(false);
            setPendingAccountSetup(null);
            hasProcessedInitialAssetGroupRef.current = false; // Reset when dialog closes
        }
    }, [open, reportDate, depreciationDifference, initialSortCodeId, initialAssetGroup, journalEntries, parentSortCodeIdOptions, selectedAssetGroups, getAssetGroupDepreciationDifference]);
    
    // Handle initialAssetGroup: check if debit/credit accounts exist and pre-fill or open AddAccountCardModal
    useEffect(() => {
        if (!open || !initialAssetGroup || !localDebitAccountOptions.length || hasProcessedInitialAssetGroupRef.current) return;
        
        const debitAccountName = `${Constants.DepreciationPrefixes.DebitAccount}${initialAssetGroup.assetGroupName}`;
        const creditAccountName = `${Constants.DepreciationPrefixes.CreditAccount}${initialAssetGroup.assetGroupName}`;
        
        // Helper function to normalize account names for comparison (trim whitespace)
        const normalizeName = (name) => (name || '').trim();
        
        // Find existing accounts - check if name contains the expected prefix and asset group name
        const debitPrefix = normalizeName(Constants.DepreciationPrefixes.DebitAccount);
        const creditPrefix = normalizeName(Constants.DepreciationPrefixes.CreditAccount);
        const assetGroupName = normalizeName(initialAssetGroup.assetGroupName);
        
        const existingDebitAccount = localDebitAccountOptions.find(
            account => {
                const accountName = normalizeName(account.name || '');
                const expectedName = normalizeName(debitAccountName);
                // Check exact match, or if account name contains both prefix and asset group name
                const matches = accountName === expectedName || 
                       (accountName.includes(debitPrefix) && accountName.includes(assetGroupName));
                if (matches) {
                    console.log('Found debit account:', account);
                }
                return matches;
            }
        );
        const existingCreditAccount = localDebitAccountOptions.find(
            account => {
                const accountName = normalizeName(account.name || '');
                const expectedName = normalizeName(creditAccountName);
                // Check exact match, or if account name contains both prefix and asset group name
                const matches = accountName === expectedName || 
                       (accountName.includes(creditPrefix) && accountName.includes(assetGroupName));
                if (matches) {
                    console.log('Found credit account:', account);
                }
                return matches;
            }
        );

        
        // Set up accounts: first debit, then credit
        if (!existingDebitAccount) {
            // Debit account doesn't exist - open AddAccountCardModal
            hasProcessedInitialAssetGroupRef.current = true; // Mark as processed
            setIsSettingUpAccounts(true);
            setPendingAccountSetup('debit');
            setEditedAddAccountCard(true);
            setIsEditAccountCard(false);
            setAddAccountCardType('debitAccount');
            // Use getNextUniqueCardId() to ensure uniqueness
            const uniqueDebitCardId = getNextUniqueCardId();
            setNewAccountCard({
                accountCodeName: debitAccountName,
                accountCodeId: uniqueDebitCardId.toString(),
                accountCaseId: '',
            });
        } else if (!existingCreditAccount) {
            // Debit exists but credit doesn't - set debit first, then open AddAccountCardModal for credit
            hasProcessedInitialAssetGroupRef.current = true; // Mark as processed
            handleFormChange('debitAccount', existingDebitAccount.cardId);
            setIsSettingUpAccounts(true);
            setPendingAccountSetup('credit');
            setEditedAddAccountCard(true);
            setIsEditAccountCard(false);
            setAddAccountCardType('creditAccount');
            // Use getNextUniqueCardId() to ensure uniqueness (recalculate after debit account exists)
            const uniqueCreditCardId = getNextUniqueCardId();
            setNewAccountCard({
                accountCodeName: creditAccountName,
                accountCodeId: uniqueCreditCardId.toString(),
                accountCaseId: '',
            });
        } else {
            // Both exist - just set them
            hasProcessedInitialAssetGroupRef.current = true; // Mark as processed
            handleFormChange('debitAccount', existingDebitAccount.cardId);
            handleFormChange('creditAccount', existingCreditAccount.cardId);
        }
    }, [open, initialAssetGroup, localDebitAccountOptions, getNextUniqueCardId]);


    // Create enhanced options with "Add New" as first item
    const enhancedSortCodeIdOptions = useMemo(() => {
        const addNewOption = {
            sortCodeId: 'ADD_NEW_SORT_CODE',
            sortCodeName: t('NewSortCode'),
            isEditable: false,
            isAddNew: true
        };
        return [addNewOption, ...localSortCodeIdOptions];
    }, [localSortCodeIdOptions, t]);

    const enhancedDebitAccountOptions = useMemo(() => {
        const addNewOption = {
            cardId: 'ADD_NEW_ACCOUNT',
            name: t('AddAccountCard'),
            isEditable: false,
            isAddNew: true
        };
        return [addNewOption, ...localDebitAccountOptions];
    }, [localDebitAccountOptions, t]);

    const enhancedCreditAccountOptions = useMemo(() => {
        const addNewOption = {
            cardId: 'ADD_NEW_ACCOUNT',
            name: t('AddAccountCard'),
            isEditable: false,
            isAddNew: true
        };
        return [addNewOption, ...localDebitAccountOptions];
    }, [localDebitAccountOptions, t]);

    // Calculate max cardId for auto-increment
    useEffect(() => {
        if (localDebitAccountOptions.length > 0) {
            const maxCardId = Math.max(
                ...localDebitAccountOptions.map((opt) => parseInt(opt.cardId, 10) || 0)
            );
            setCardIdOption(maxCardId + 1);
        } else {
            setCardIdOption(1);
        }
    }, [localDebitAccountOptions]);

    // Called when user types in an unknown account & clicks "AddAccountCard"
    const handleNoOptionCreditDebitClick = (searchTerm, fieldName) => {
        setEditedAddAccountCard(true);
        setIsEditAccountCard(false);
        setAddAccountCardType(fieldName);
        
        // Set prefix based on field type
        let prefix = '';
        if (fieldName === 'debitAccount') {
            prefix = Constants.DepreciationPrefixes.DebitAccount;
        } else if (fieldName === 'creditAccount') {
            prefix = Constants.DepreciationPrefixes.CreditAccount;
        }
        
        // Use getNextUniqueCardId() to ensure uniqueness
        const uniqueCardId = getNextUniqueCardId();
        setNewAccountCard({
            accountCodeName: prefix,
            accountCodeId: uniqueCardId.toString(),
            accountCaseId: '',
        });
    };

    // When the user **successfully adds** a new card in the modal
    // we set that new card ID to the correct field (credit/debit) in our form
    // and update the account options list
    const handleChangeAfterAdd = async (newlyAddedCardId, fieldName) => {
        // Update the form field
        handleFormChange(fieldName, newlyAddedCardId);
        
                    // Fetch updated account cards from server to refresh the options
                    if (client && client.caseId && token) {
                        try {
                            const response = await EnumsService.GetAccountCardsByCaseId(client.caseId, token);
                            if (response.ok) {
                                const accountsData = await response.json();
                                setLocalDebitAccountOptions(accountsData);
                                
                                // Also refresh enums in HamburgerMenu to update TransactionSearchResultModal
                                if (refreshEnumsCallback) {
                                    refreshEnumsCallback();
                                }
                    
                    // If we're auto-setting up accounts from initialAssetGroup
                    if (isSettingUpAccounts && initialAssetGroup) {
                        if (pendingAccountSetup === 'debit') {
                            // Debit account was just added, now check if credit account exists
                            const creditAccountName = `${Constants.DepreciationPrefixes.CreditAccount}${initialAssetGroup.assetGroupName}`;
                            // Helper function to normalize account names for comparison
                            const normalizeName = (name) => (name || '').trim();
                            const creditPrefix = normalizeName(Constants.DepreciationPrefixes.CreditAccount);
                            const assetGroupName = normalizeName(initialAssetGroup.assetGroupName);
                            const expectedCreditName = normalizeName(creditAccountName);
                            const existingCreditAccount = accountsData.find(
                                account => {
                                    const accountName = normalizeName(account.name || '');
                                    // Check exact match, or if account name contains both prefix and asset group name
                                    return accountName === expectedCreditName || 
                                           (accountName.includes(creditPrefix) && accountName.includes(assetGroupName));
                                }
                            );
                            
                            if (existingCreditAccount) {
                                // Credit account exists - set it and finish setup
                                handleFormChange('creditAccount', existingCreditAccount.cardId);
                                setIsSettingUpAccounts(false);
                                setPendingAccountSetup(null);
                            } else {
                                // Credit account doesn't exist - open AddAccountCardModal for credit
                                setPendingAccountSetup('credit');
                                setEditedAddAccountCard(true);
                                setIsEditAccountCard(false);
                                setAddAccountCardType('creditAccount');
                                // Calculate next unique cardId using fresh accountsData
                                const existingCardIds = accountsData
                                    .map((opt) => parseInt(opt.cardId, 10))
                                    .filter((id) => !isNaN(id) && id > 0)
                                    .sort((a, b) => b - a);
                                const maxCardId = existingCardIds.length > 0 ? existingCardIds[0] : 0;
                                let nextId = maxCardId + 1;
                                // Double-check for uniqueness (in case of gaps)
                                while (existingCardIds.includes(nextId)) {
                                    nextId++;
                                }
                                setNewAccountCard({
                                    accountCodeName: creditAccountName,
                                    accountCodeId: nextId.toString(),
                                    accountCaseId: '',
                                });
                            }
                        } else if (pendingAccountSetup === 'credit') {
                            // Credit account was just added - finish setup
                            setIsSettingUpAccounts(false);
                            setPendingAccountSetup(null);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching updated account cards:', error);
                // Fallback: manually add the new account card to the list
                // Construct account card object from newAccountCard state
                const newAccountCardObject = {
                    cardId: newlyAddedCardId,
                    name: newAccountCard.accountCodeName || '',
                    accountCaseId: newAccountCard.accountCaseId || '',
                    // Add any other default properties that might be needed
                };
                setLocalDebitAccountOptions(prev => [...prev, newAccountCardObject]);
                
                // If we're auto-setting up accounts, reset the state
                if (isSettingUpAccounts) {
                    setIsSettingUpAccounts(false);
                    setPendingAccountSetup(null);
                }
            }
        }
    };

    // Close handler for AddAccountCardModal
    const handleCloseItem = () => {
        setEditedAddAccountCard(false);
        // If we were auto-setting up accounts and user closed without saving, reset state
        if (isSettingUpAccounts) {
            setIsSettingUpAccounts(false);
            setPendingAccountSetup(null);
        }
    };

    // Create accountCardOptions from localDebitAccountOptions for AddAccountCardModal
    const accountCardOptions = useMemo(() => {
        return [{ name: t('AddAccountCard'), value1: 'AddItem' }, ...localDebitAccountOptions];
    }, [localDebitAccountOptions, t]);

    // Minimal fetchEnums - refreshes account options if needed
    // Note: This is a simplified version. Parent component should pass a full fetchEnums if needed.
    const fetchEnums = async () => {
        // This is a placeholder - parent component should ideally pass fetchEnums prop
        // to properly refresh all options after adding an account card
        console.log('fetchEnums called - consider passing fetchEnums from parent component');
    };

    // Handle successful sort code creation
    const handleSortCodeSaveSuccess = async (newSortCodeId) => {
        // Fetch updated sort codes from server
        if (client && client.accountantId && token) {
            try {
                const response = await EnumsService.GetSortCodesByAccountantId(client.accountantId, token);
                if (response.ok) {
                    const sortCodesData = await response.json();
                    setLocalSortCodeIdOptions(sortCodesData);
                    
                    // Automatically select the newly created sort code
                    if (newSortCodeId) {
                        handleFormChange('sortCodeId', newSortCodeId);
                    }
                    
                    // Also refresh enums in HamburgerMenu to update TransactionSearchResultModal
                    if (refreshEnumsCallback) {
                        refreshEnumsCallback();
                    }
                }
            } catch (error) {
                console.error('Error fetching updated sort codes:', error);
            }
        }
    };

    const handleFormChange = (field, value) => {
        // Special handling for amount field - validate against maxDepreciationDifference
        if (field === 'amount') {
            const numValue = parseFloat(value) || 0;
            const isValid = numValue <= maxAllowedAmount;
            setAmountError(!isValid);
            
            // Show error toast if amount exceeds maximum
            if (!isValid && numValue > maxAllowedAmount) {
                toast.error(
                    t('AmountExceedsDepreciationDifference') || 
                    `Amount cannot exceed the depreciation difference (${maxAllowedAmount})`
                );
            }
        }
        
        setCreateFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const findAccountCard = (cardId) =>
        cardId ? localDebitAccountOptions.find((o) => String(o.cardId) === String(cardId)) : null;
    const hasPalCode = (card) => !!(card?.palCode ?? card?.PalCode);
    const missingPalCode = {
        debitAccount: !!createFormData.debitAccount && !hasPalCode(findAccountCard(createFormData.debitAccount)),
        creditAccount: !!createFormData.creditAccount && !hasPalCode(findAccountCard(createFormData.creditAccount)),
    };

    const openPalCodeEditor = (fieldName) => {
        const card = findAccountCard(createFormData[fieldName]);
        if (!card) return;
        setIsSettingUpAccounts(false);
        setPendingAccountSetup(null);
        setIsEditAccountCard(true);
        setAddAccountCardType(fieldName);
        setNewAccountCard({
            ...card,
            accountCodeId: String(card.cardId),
            accountCodeName: card.name || '',
            accountCaseId: card.accountCaseId || '',
        });
        setEditedAddAccountCard(true);
    };

    const handleSave = async () => {
        // Validate required fields
        if (!createFormData.sortCodeId || !createFormData.documentDate || !createFormData.uploadDate || !createFormData.amount
            || !createFormData.debitAccount || !createFormData.creditAccount) {
            toast.error(t('PleaseFillAllRequiredFields') || 'Please fill all required fields');
            return;
        }

        const fieldMissingPalCode = ['debitAccount', 'creditAccount'].find((f) => missingPalCode[f]);
        if (fieldMissingPalCode) {
            toast.error(t('assetsPage.accountMissingPalCode'));
            openPalCodeEditor(fieldMissingPalCode);
            return;
        }
        
        // Validate amount doesn't exceed depreciation difference
        const amountValue = parseFloat(createFormData.amount) || 0;
        if (amountValue > maxAllowedAmount) {
            toast.error(
                t('AmountExceedsDepreciationDifference') || 
                `Amount cannot exceed the depreciation difference (${maxAllowedAmount})`
            );
            setAmountError(true);
            return;
        }

        if (setLoading) setLoading(true);
        try {
            // Prepare the payload for creating journal entry (matching JournalEntry.jsx structure)
            // Helper to convert date to ISO string
            const toISOString = (date) => {
                if (!date) return null;
                if (typeof date === 'string') return date;
                if (dayjs.isDayjs(date)) return date.toISOString();
                return new Date(date).toISOString();
            };

            const payload = {
                caseId: client.caseId,
                accountantId: client.accountantId,
                sortCodeId: createFormData.sortCodeId,
                reference: createFormData.reference || '',
                reference2: createFormData.reference2 || '',
                description: createFormData.description || '',
                amount: parseFloat(createFormData.amount) || 0,
                documentDate: toISOString(createFormData.documentDate) || new Date().toISOString(),
                reportingMonthDate: toISOString(createFormData.reportingMonthDate) || (reportDate ? dayjs.utc(reportDate).toISOString() : new Date().toISOString()),
                registrationMonthDate: toISOString(createFormData.registrationMonthDate) || (reportDate ? dayjs.utc(reportDate).toISOString() : new Date().toISOString()),
                creditAccount: createFormData.creditAccount || '',
                debitAccount: createFormData.debitAccount || '',
                vat: createFormData.vat ?? 0, // Default to 0 for depreciation journal entries
                confirmationNumber: createFormData.confirmationNumber || '',
                accountCaseId: createFormData.accountCaseId || '',
                uploadDate: toISOString(createFormData.uploadDate) || new Date().toISOString(),
                foreignCurrencyAmount: createFormData.foreignCurrencyAmount ?? null,
                foreignCurrencyCode: createFormData.foreignCurrencyCode ?? null,
                journalEntryType: 1, // IsDepreciationJournal
                addAsProcessed: true
            };

            // Create the journal entry using DocumentService
            const response = await DocumentService.UploadDocumentPayload('JournalEntries', payload, token);

            if (response.ok) {
                toast.success(t('documentUploadSuccess') || 'Journal entry created successfully');
                
                // Refetch all enums (account cards and sort codes) after journal entry is created
                try {
                    const [accountCardsResponse, sortCodesResponse] = await Promise.all([
                        EnumsService.GetAccountCardsByCaseId(client.caseId, token),
                        EnumsService.GetSortCodesByAccountantId(client.accountantId, token)
                    ]);

                    if (accountCardsResponse.ok) {
                        const accountsData = await accountCardsResponse.json();
                        setLocalDebitAccountOptions(accountsData);
                    }

                    if (sortCodesResponse.ok) {
                        const sortCodesData = await sortCodesResponse.json();
                        setLocalSortCodeIdOptions(sortCodesData);
                    }

                    // Also refresh enums in HamburgerMenu to update TransactionSearchResultModal
                    if (refreshEnumsCallback) {
                        refreshEnumsCallback();
                    }
                } catch (enumError) {
                    console.error('Error refetching enums after journal entry creation:', enumError);
                    // Don't show error toast here - journal entry was created successfully
                    // Just log the error and continue
                }
                
                // Call success callback to refresh data BEFORE closing dialog
                if (onSaveSuccess) {
                    await onSaveSuccess();
                }
                
                // Close this dialog (but keep main modal open)
                // onClose();
            } else {
                const errorText = await response.text();
                toast.error(t('documentUploadError') || `Error: ${errorText}`);
            }
        } catch (error) {
            console.error('Error creating journal entry:', error);
            toast.error(t('documentUploadError') || 'Error creating journal entry');
        } finally {
            if (setLoading) setLoading(false);
        }
    };

    const fieldInputProps = xtramInputProps(direction);
    const fieldTextAlign = xtramTextAlign(direction);
    const canSave = !!createFormData.sortCodeId && !!createFormData.documentDate && !!createFormData.uploadDate
        && !!createFormData.amount && !!createFormData.debitAccount && !!createFormData.creditAccount
        && !loading && !amountError;
    const palCodeNotice = (fieldName) => missingPalCode[fieldName] && (
        <div className={`${shell.notice} ${shell.noticeDanger}`}>
            <span>{t('assetsPage.accountMissingPalCode')}</span>
            <button type="button" className={shell.linkBtn} onClick={() => openPalCodeEditor(fieldName)}>
                {t('assetsPage.updatePalCode')}
            </button>
        </div>
    );
    const toDate = (value) => (value ? (typeof value === 'string' ? new Date(value) : value) : null);
    const label = (text, required = false, extra = null) => (
        <span className={shell.fieldLabel}>
            <span>{required && <span className={shell.requiredMark}>*</span>}{text}</span>
            {extra}
        </span>
    );

    if (!open) return null;

    return (
        <>
            <AssetModalShell
                open={open}
                onClose={onClose}
                title={t('CreateDepreciationJournalEntry')}
                direction={direction}
                size="md"
                zIndex={1300}
                footer={(
                    <>
                        <button type="button" className={shell.secondaryBtn} onClick={onClose}>
                            {t('Cancel')}
                        </button>
                        <button type="button" className={shell.primaryBtn} onClick={handleSave} disabled={!canSave}>
                            <SaveIcon fontSize="small" />
                            {t('Save')}
                        </button>
                    </>
                )}
            >
                <section className={shell.section}>
                    <h3 className={shell.sectionTitle}>{t('Accounts')}</h3>
                    <div className={shell.fieldGrid}>
                        <div className={`${shell.field} ${shell.fieldFull}`}>
                            {label(t('Code ID'), true)}
                            <Autocomplete
                                options={enhancedSortCodeIdOptions}
                                getOptionLabel={(option) => {
                                    if (option.isAddNew) return option.sortCodeName;
                                    return `${option.sortCodeId} - ${option.sortCodeName}`;
                                }}
                                value={
                                    enhancedSortCodeIdOptions.find(
                                        (option) => option.sortCodeId === createFormData.sortCodeId
                                    ) || null
                                }
                                onChange={(event, newValue) => {
                                    if (newValue && newValue.isAddNew) {
                                        // User clicked the "Add New Sort Code" option
                                        setIsCreateSortCodeDialogOpen(true);
                                        return;
                                    }
                                    const newSortCodeId = newValue ? newValue.sortCodeId : '';
                                    handleFormChange('sortCodeId', newSortCodeId);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('Code ID')}
                                        variant="standard"
                                        InputProps={{
                                            ...params.InputProps,
                                            ...fieldInputProps,
                                        }}
                                        inputProps={{
                                            ...params.inputProps,
                                            ...fieldTextAlign,
                                        }}
                                        required
                                    />
                                )}
                                renderOption={(props, option) => {
                                    if (option.isAddNew) {
                                        return (
                                            <StyledListItem
                                                {...props}
                                                style={{
                                                    fontWeight: 'bold',
                                                    color: '#304FFF',
                                                    borderBottom: '1px solid #ddd',
                                                }}
                                            >
                                                ➕ {option.sortCodeName}
                                            </StyledListItem>
                                        );
                                    }
                                    return (
                                        <Tooltip title={option.isEditable ? t('RightClickToEdit') : t('NotEditable')} placement="top">
                                            <StyledListItem {...props}>
                                                {`${option.sortCodeId} - ${option.sortCodeName}`}
                                            </StyledListItem>
                                        </Tooltip>
                                    );
                                }}
                                PopperComponent={(props) => (
                                    <StyledPopper {...props} style={{ ...props.style, direction }} />
                                )}
                            />
                        </div>

                        <div className={shell.field}>
                            {label(t('Debit Account'), true)}
                            <Autocomplete
                                options={enhancedDebitAccountOptions}
                                getOptionLabel={(o) => {
                                    if (o.isAddNew) return o.name;
                                    return `${o.cardId} - ${o.name}`;
                                }}
                                value={enhancedDebitAccountOptions.find((o) => o.cardId === createFormData.debitAccount) || null}
                                onChange={(e, v) => {
                                    if (v && v.isAddNew) {
                                        // User clicked the "Add New Account" option
                                        handleNoOptionCreditDebitClick('', 'debitAccount');
                                        return;
                                    }
                                    const id = v ? v.cardId : null;
                                    handleFormChange('debitAccount', id);
                                }}
                                onInputChange={(e, v) => setSearchTermDebit(v)}
                                noOptionsText={
                                    <Button
                                        onClick={() => handleNoOptionCreditDebitClick(searchTermDebit, 'debitAccount')}
                                        variant="contained"
                                        color="primary"
                                    >
                                        {t('AddAccountCard')} "{searchTermDebit}"
                                    </Button>
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('Debit Account')}
                                        variant="standard"
                                        InputProps={{
                                            ...params.InputProps,
                                            ...fieldInputProps,
                                        }}
                                        inputProps={{
                                            ...params.inputProps,
                                            ...fieldTextAlign,
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => {
                                    if (option.isAddNew) {
                                        return (
                                            <StyledListItem
                                                {...props}
                                                style={{
                                                    fontWeight: 'bold',
                                                    color: '#304FFF',
                                                    borderBottom: '1px solid #ddd',
                                                }}
                                            >
                                                ➕ {option.name}
                                            </StyledListItem>
                                        );
                                    }
                                    return (
                                        <Tooltip title={option.isEditable ? t('RightClickToEdit') : t('NotEditable')} placement="top">
                                            <StyledListItem {...props}>
                                                {`${option.cardId} - ${option.name}`}
                                            </StyledListItem>
                                        </Tooltip>
                                    );
                                }}
                                PopperComponent={(props) => (
                                    <StyledPopper {...props} style={{ ...props.style, direction }} />
                                )}
                            />
                            {palCodeNotice('debitAccount')}
                        </div>

                        <div className={shell.field}>
                            {label(t('Credit Account'), true)}
                            <Autocomplete
                                options={enhancedCreditAccountOptions}
                                getOptionLabel={(o) => {
                                    if (o.isAddNew) return o.name;
                                    return `${o.cardId} - ${o.name}`;
                                }}
                                value={enhancedCreditAccountOptions.find((o) => o.cardId === createFormData.creditAccount) || null}
                                onChange={(e, v) => {
                                    if (v && v.isAddNew) {
                                        // User clicked the "Add New Account" option
                                        handleNoOptionCreditDebitClick('', 'creditAccount');
                                        return;
                                    }
                                    const id = v ? v.cardId : null;
                                    handleFormChange('creditAccount', id);
                                }}
                                onInputChange={(e, v) => setSearchTermCredit(v)}
                                noOptionsText={
                                    <Button
                                        onClick={() => handleNoOptionCreditDebitClick(searchTermCredit, 'creditAccount')}
                                        variant="contained"
                                        color="primary"
                                    >
                                        {t('AddAccountCard')} "{searchTermCredit}"
                                    </Button>
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('Credit Account')}
                                        variant="standard"
                                        InputProps={{
                                            ...params.InputProps,
                                            ...fieldInputProps,
                                        }}
                                        inputProps={{
                                            ...params.inputProps,
                                            ...fieldTextAlign,
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => {
                                    if (option.isAddNew) {
                                        return (
                                            <StyledListItem
                                                {...props}
                                                style={{
                                                    fontWeight: 'bold',
                                                    color: '#304FFF',
                                                    borderBottom: '1px solid #ddd',
                                                }}
                                            >
                                                ➕ {option.name}
                                            </StyledListItem>
                                        );
                                    }
                                    return (
                                        <Tooltip title={option.isEditable ? t('RightClickToEdit') : t('NotEditable')} placement="top">
                                            <StyledListItem {...props}>
                                                {`${option.cardId} - ${option.name}`}
                                            </StyledListItem>
                                        </Tooltip>
                                    );
                                }}
                                PopperComponent={(props) => (
                                    <StyledPopper {...props} style={{ ...props.style, direction }} />
                                )}
                            />
                            {palCodeNotice('creditAccount')}
                        </div>
                    </div>
                </section>

                <section className={shell.section}>
                    <h3 className={shell.sectionTitle}>{t('Dates')}</h3>
                    <div className={shell.fieldGrid}>
                        <div className={shell.field}>
                            {label(t('MonthReportDate'))}
                            <CustomMonthDatePicker
                                date={createFormData.reportingMonthDate ? dayjs(new Date(createFormData.reportingMonthDate)) : null}
                                onDateChange={(date) => {
                                    if (date) handleFormChange('reportingMonthDate', date);
                                }}
                                height="var(--xtram-control-height, 40px)"
                            />
                        </div>
                        <div className={shell.field}>
                            {label(t('RegistrationMonthDate'))}
                            <CustomMonthDatePicker
                                date={createFormData.registrationMonthDate ? dayjs(new Date(createFormData.registrationMonthDate)) : null}
                                onDateChange={(date) => {
                                    if (date) handleFormChange('registrationMonthDate', date);
                                }}
                                height="var(--xtram-control-height, 40px)"
                            />
                        </div>
                        <div className={shell.field}>
                            {label(t('DocumentDate'), true)}
                            <CustomDatePicker
                                date={toDate(createFormData.documentDate)}
                                onDateChange={(date) => {
                                    if (date) handleFormChange('documentDate', date);
                                }}
                                height="var(--xtram-control-height, 40px)"
                                inputSx={xtramDateInputSx()}
                            />
                        </div>
                        <div className={shell.field}>
                            {label(t('RegistrationDate'), true)}
                            <CustomDatePicker
                                date={toDate(createFormData.uploadDate)}
                                onDateChange={(date) => {
                                    if (date) handleFormChange('uploadDate', date);
                                }}
                                height="var(--xtram-control-height, 40px)"
                                inputSx={xtramDateInputSx()}
                            />
                        </div>
                    </div>
                </section>

                <section className={shell.section}>
                    <h3 className={shell.sectionTitle}>{t('Amount')}</h3>
                    <div className={shell.field}>
                        {label(
                            t('Amount'),
                            true,
                            maxAllowedAmount > 0 && (
                                <span className={`${shell.badge} ${shell.badgeNeutral}`}>
                                    {t('Max')}: {beautifyNumber(maxAllowedAmount)}
                                </span>
                            )
                        )}
                        <TextField
                            fullWidth
                            placeholder={t('Amount')}
                            value={createFormData.amount || ''}
                            onChange={(e) => handleFormChange('amount', e.target.value)}
                            variant="standard"
                            type="number"
                            InputProps={xtramInputProps(direction, amountError)}
                            inputProps={fieldTextAlign}
                            required
                            error={amountError}
                            helperText={amountError ? (t('AmountExceedsDepreciationDifference') || `Amount cannot exceed ${maxAllowedAmount}`) : ''}
                        />
                    </div>
                </section>
            </AssetModalShell>

            {/* AddAccountCardModal */}
            <AddAccountCardModal
                accountCardOptions={accountCardOptions}
                open={editedAddAccountCard}
                onClose={handleCloseItem}
                editedItem={newAccountCard}
                isEdit={isEditAccountCard}
                isRange={false}
                isClosedReportingMonth={false}
                palCodes={[]}
                propPalCode=""
                setPalCode={() => {}}
                direction={direction}
                isAdd={true}
                client={client}
                token={token}
                setLoading={setLoading}
                handleChangeAfterAdd={handleChangeAfterAdd}
                fetchEnums={fetchEnums}
                type={addAccountCardType}
                assetGroups={assetGroups}
                requirePalCode
            />

            {/* CreateDepreciationSortCodeDialog */}
            <CreateDepreciationSortCodeDialog
                open={isCreateSortCodeDialogOpen}
                onClose={() => setIsCreateSortCodeDialogOpen(false)}
                client={client}
                token={token}
                direction={direction}
                existingSortCodes={localSortCodeIdOptions}
                assetGroups={assetGroups}
                onSaveSuccess={handleSortCodeSaveSuccess}
            />
        </>
    );
};

export default CreateDepreciationJournalEntryDialog;

